import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { useContext, useEffect, useState } from "react";
import {
  BentoBrand,
  BentoContact,
  EmailSequence,
  EmailSequenceMap,
  SavedBrandCollection,
} from "schemas/dashboard";

import BulkSendDialog from "components/BulkSendDialog";
import { fetcherAuth } from "utils/api";
import { getCategoryName } from "utils/templates";
import { trackEvent } from "utils/tracking";

import ReviewBrands from "./ReviewBrands";
import UploadForm from "./UploadForm";
import YourContacts, { collectUserSuppliedContacts } from "./YourContacts";
import { UploadResult, UploadedBrand } from "./schema";

const DEFAULT_COLLECTION_NAME = "New Pitch List";

type Step = "form" | "your-contacts" | "review";

interface Props {
  onClose?: () => void;
}

const UploadBrands = ({ onClose }: Props) => {
  const { getToken } = useAuth();
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { setDiscoverTab } = useContext(DiscoverViewContext);
  const { setNeedsRefreshList, fetchCollections } = useContext(
    SavedBrandCollectionsContext,
  );

  const [step, setStep] = useState<Step>("form");
  const [collectionName, setCollectionName] = useState(DEFAULT_COLLECTION_NAME);
  const [uploadResult, setUploadResult] = useState<UploadResult>({
    brands: [],
    unmatched: [],
    optedOutContacts: [],
    wishlistedBrands: [],
  });
  const [selectedContacts, setSelectedContacts] = useState<
    Record<number, BentoContact | null>
  >({});
  const [saving, setSaving] = useState(false);
  const [savedCollectionId, setSavedCollectionId] = useState<number | null>(
    null,
  );
  const [bulkSendOpen, setBulkSendOpen] = useState(false);

  useEffect(() => {
    if (step !== "review") return;
    const defaults: Record<number, BentoContact | null> = {};
    for (const brand of uploadResult.brands) {
      if (selectedContacts[brand.bentoBrand.id] !== undefined) continue;
      defaults[brand.bentoBrand.id] = brand.contacts[0] ?? null;
    }
    if (Object.keys(defaults).length > 0) {
      setSelectedContacts((prev) => ({ ...defaults, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, uploadResult]);

  const handleUploaded = async (result: UploadResult) => {
    setUploadResult(result);
    setSelectedContacts({});

    const hasUserSuppliedContacts =
      collectUserSuppliedContacts(result.brands).length > 0;
    const needsContactsScreen =
      hasUserSuppliedContacts && !currentOrg?.allowShareContacts;
    setStep(needsContactsScreen ? "your-contacts" : "review");
  };

  const handleBack = () => setStep("form");

  const exitFlow = () => {
    if (onClose) {
      onClose();
    } else {
      setDiscoverTab(DiscoverTabView.SAVED_BRANDS);
    }
  };

  const handleShareWithCommunity = async () => {
    if (!currentOrg?.id) return;
    trackEvent("Upload Brands - Share Contacts", {
      Option: "Share With Community",
    });
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/settings/contacts`,
        "PUT",
        {},
        { allowShareContacts: true },
      );
      setCurrentOrg((prev) =>
        prev ? { ...prev, allowShareContacts: true } : prev,
      );

      setStep("review");
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleKeepPrivate = () => {
    trackEvent("Upload Brands - Share Contacts", { Option: "Keep Private" });
    setStep("review");
  };

  const handleDecideLater = () => {
    trackEvent("Upload Brands - Share Contacts", { Option: "Decide Later" });
    setStep("review");
  };

  const saveCollection = async (
    brands: UploadedBrand[],
    name: string,
  ): Promise<SavedBrandCollection | null> => {
    if (!currentOrg?.id) return null;
    const bentoBrandIds = brands.map((b) => b.bentoBrand.id);
    if (bentoBrandIds.length === 0) {
      setAlert("No brands to save", "warning");
      return null;
    }

    trackEvent("Upload Brands - Save Collection", {
      "Number of Brands": bentoBrandIds.length,
    });
    const res = await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/saved-brand-collections/from-upload`,
      "POST",
      {},
      { name: name.trim() || DEFAULT_COLLECTION_NAME, bentoBrandIds },
    );
    setNeedsRefreshList(true);
    fetchCollections();
    return res?.savedBrandCollection ?? null;
  };

  const persistCollection = (): Promise<SavedBrandCollection | null> =>
    saveCollection(uploadResult.brands, collectionName);

  const handleJustSave = async () => {
    if (saving) return;
    setSaving(true);
    trackEvent("Upload Brands - Save Only Button Clicked");
    try {
      const collection = await persistCollection();
      if (collection === null) return;
      setAlert("Pitch list saved", "success");
      exitFlow();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPitch = async () => {
    if (saving) return;
    setSaving(true);
    trackEvent("Upload Brands - Save and Pitch Button Clicked");
    try {
      const collection = await persistCollection();
      if (collection === null) return;
      setSavedCollectionId(collection.id);
      setBulkSendOpen(true);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSaving(false);
    }
  };

  const presetEmailSequenceMap: EmailSequenceMap = uploadResult.brands.reduce(
    (acc, brand) => {
      const sequence: EmailSequence = {
        bentoBrandId: brand.bentoBrand.id,
        category: getCategoryName(brand.bentoBrand),
        bentoBrand: brand.bentoBrand,
      };
      acc[brand.bentoBrand.id] = sequence;
      return acc;
    },
    {} as EmailSequenceMap,
  );

  const handleBulkSendClose = () => {
    setBulkSendOpen(false);
    exitFlow();
  };

  const handleBulkSendReset = () => {
    setBulkSendOpen(false);
    exitFlow();
  };

  const handleRemoveBrandFromBulk = (bentoBrand: BentoBrand) => {
    trackEvent("Upload Brands - Remove Brand From Bulk Send", {
      "Brand ID": bentoBrand.id,
      "Brand Name": bentoBrand.brandName,
    });
    setUploadResult((prev) => ({
      ...prev,
      brands: prev.brands.filter((b) => b.bentoBrand.id !== bentoBrand.id),
    }));
    setSelectedContacts((prev) => {
      const next = { ...prev };
      delete next[bentoBrand.id];
      return next;
    });
  };

  if (step === "form") {
    return (
      <UploadForm
        collectionName={collectionName}
        setCollectionName={setCollectionName}
        onUploaded={handleUploaded}
        onClose={onClose}
      />
    );
  }

  if (step === "your-contacts") {
    return (
      <YourContacts
        brands={uploadResult.brands}
        onBack={handleBack}
        onShareWithCommunity={handleShareWithCommunity}
        onKeepPrivate={handleKeepPrivate}
        onDecideLater={handleDecideLater}
      />
    );
  }

  return (
    <>
      <ReviewBrands
        brands={uploadResult.brands}
        wishlistBrandNames={[
          ...uploadResult.wishlistedBrands,
          ...uploadResult.unmatched,
        ]}
        optedOutContacts={uploadResult.optedOutContacts}
        collectionName={collectionName}
        setCollectionName={setCollectionName}
        selectedContacts={selectedContacts}
        onSelectContact={(bentoBrandId, contact) => {
          trackEvent("Upload Brands - Select Contact", {
            "Brand ID": bentoBrandId,
            "Contact ID": contact?.id,
          });
          setSelectedContacts((prev) => ({
            ...prev,
            [bentoBrandId]: contact,
          }));
        }}
        onBack={handleBack}
        onSaveAndPitch={handleSaveAndPitch}
        onJustSave={handleJustSave}
        saving={saving}
      />
      <BulkSendDialog
        open={bulkSendOpen}
        handleClose={handleBulkSendClose}
        resetSelectedBrands={handleBulkSendReset}
        presetEmailSequenceMap={presetEmailSequenceMap}
        presetSelectedContacts={selectedContacts}
        collectionId={savedCollectionId ?? undefined}
        removeFromBulkBrands={handleRemoveBrandFromBulk}
      />
    </>
  );
};

export default UploadBrands;
